import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
// Every nav-link row gets its pin star; other links pass through as plain Links.
import Link, { NavPinsContext } from '../../pins/PinnableNavLink';
import usePins from '../../pins/usePins';
import FeatherIcon from '../../common/FeatherIcon';
import { SurfaceIcon } from '../../common/surfaceIcons';
import AgentMark from '../../common/AgentMark';
import organizationService from '../../../services/organization/organizationService';

import {
  PRODUCTS,
  CUSTOM_AGENTS_DASHBOARD_PATH,
  GEMINI_ENTERPRISE_DASHBOARD_LABEL,
  GEMINI_ENTERPRISE_DASHBOARD_PATH,
  GEMINI_WORKSPACE_DASHBOARD_LABEL,
  GEMINI_WORKSPACE_DASHBOARD_PATH,
  detectProductFromPath,
} from './productConfig';
import useModuleLicense from '../../../licensing/useModuleLicense';
import NavLock from '../../licensing/NavLock';
import RowLock, { ContainerLock, LockedParent, moduleLabelFor } from '../../licensing/RowLock';
import { isNavPathLocked } from '../../../licensing/navModules';

import { readNormalizedUserFromStorage } from '../../../utils/entitlements';

import { isFeatureEnabled } from '../../../utils/featureFlags';
import { toLegacyPath } from '../../../pages/custom-agents/customAgentsRoutes';
import { hasCapability } from '../../../pages/custom-agents/customAgentsModule';
import useInboxUnread from '../../../hooks/useInboxUnread';
import { canManageEndpointPolicy, isPrivilegedUser } from '../../../utils/permissions';
import PinnedNavBlock from '../../pins/PinnedNavBlock';
import useLens from '../../lens/useLens';
import useSurfaceSelection from '../../lens/surfaceSelection';
import { NAV_SECTIONS } from '../../../config/nav/catalog';
import { AllModulesToggle, LensNavCard } from '../../lens/LensNavParts';
import usePillarNav from './pillarNav';
import {
  PILLARS, PILLAR_BOTTOM, PILLAR_INBOX, PILLAR_TOP, lensKeepsItem, passesGates, pillarRoutes, trimPillars,
} from './pillarNavConfig';
import { tabFor } from './pillarTabs';
import {
  getCloudDiscoveryNavGroups,
  getCopilotNavGroups,
  getSaasAiNavGroups,
  localizeGeminiNavItem,
} from './copilotNavConfig';
import './Sidebar.css';

// Sections that ARE one product or surface wear its mark, from the same shared
// icon set Atlas, Security and the inventory draw with, so Gemini looks like
// Gemini here too (Custom Agents, Cloud Discovery and Endpoint Security have
// marks in that set as well). Brand colour, not mono, as everywhere else the
// marks sit beside a product name.
const SECTION_BRAND_SURFACE = Object.freeze({
  'enterprise-ai': 'copilot',
  'copilot-studio': 'copilot-studio',
  'gemini-enterprise-agents': 'gemini-enterprise',
  'gemini-workspace': 'gemini-workspace',
  'custom-agents': 'custom',
  'cloud-ai': 'cloud-discovery',
  'shadow-ai': 'shadow',
});

// Every other section (our modules, and multi-vendor ones such as AI Providers,
// where one vendor's logo would be wrong) keeps its Feather glyph, drawn in its
// own colour on a tint of it, so each reads at a glance in the collapsed rail.
const SECTION_COLOR = Object.freeze({
  'agent-atlas': '#7B68EE',
  monitoring: '#3987E5',
  security: '#D95926',
  finops: '#199E70',
  'model-selection': '#C98500',
  governance: '#D55181',
  'endpoint-management': '#3987E5',
  mdm: '#C98500',
  edr: '#D95926',
  'ai-gateway': '#7B68EE',
  'saas-ai-platform': '#199E70',
  sast: '#D55181',
  management: '#8B95A5',
  'custom-dashboards': '#3987E5',
  ...Object.fromEntries(PILLARS.map((p) => [p.key, p.color])),
});

// Pillar sections (the "New navigation" switch) start open: five short lists are
// the whole point of the layout. Every other section starts folded.
const PILLAR_KEYS = new Set(PILLARS.map((p) => p.key));
const isFolded = (state, key) => (state[key] === undefined ? !PILLAR_KEYS.has(key) : state[key] !== false);

// All high-level sidebar sections that can be expanded/collapsed
const SIDEBAR_SECTION_KEYS = [
  'agent-atlas',
  'cloud-ai',
  'custom-agents',
  'monitoring',
  'security',
  'finops',
  'model-selection',
  'governance',
  'shadow-ai',
  'endpoint-management',
  'ai-gateway',
  'enterprise-ai',
  'copilot-studio',
  'gemini-workspace',
  'gemini-enterprise-agents',
  'saas-ai-platform',
  'sast',
  'management',
  'custom-dashboards',
  ...PILLARS.map((p) => p.key),
];

// Consolidated AI-SOC / AI-SPM pages — rendered as a nested "Security" expander
// inside the Agent Atlas section.
// Sections every product shows, below a divider.
// (Billing & Usage moved to the account menu in the header — it is the org's
// bill, offered to its administrators only — so there is no Account Hub section.)
const ALWAYS_SHOWN_SECTIONS = ['management', 'custom-dashboards'];

const SECURITY_ATLAS_ITEMS = [
  { to: '/security-atlas/overview', icon: 'shield', labelKey: 'nav:items.overview' },
  { headingKey: 'nav:headings.triage' },
  // Signals before Incidents: signals correlate INTO incidents, so the nav follows
  // the escalation chain. (Action Queue folded into Incidents' Priority sort.)
  { to: '/security-atlas/signals', icon: 'activity', labelKey: 'nav:items.signals' },
  { to: '/security-atlas/hunt', icon: 'search', labelKey: 'nav:items.hunt' },
  { to: '/security-atlas/incidents', icon: 'alert-triangle', labelKey: 'nav:items.incidents' },
  // Response = the Approvals queue. The automation dial moved to Management: deciding how
  // much runs unattended is a settings change, not work on an open case.
  { to: '/security-atlas/response', icon: 'zap', labelKey: 'nav:items.response' },
  { to: '/security-atlas/deepfake-detector', icon: 'eye', labelKey: 'nav:items.deepfakeDetector' },
  // Posture — the skill/detection catalog + estate governance: inventory, coverage (folded
  // into AI-BOM), data security and Exceptions. (Engineer heading + Detection Health retired.)
  { headingKey: 'nav:headings.posture' },
  { to: '/security-atlas/skills', icon: 'layers', labelKey: 'nav:items.skills' },
  { to: '/security-atlas/ai-bom', icon: 'package', labelKey: 'nav:items.aiBomInventory' },
  // preview, org 3 only, until MCP capture ships (route gated the same way)
  { to: '/security-atlas/mcp-security', icon: 'server', labelKey: 'nav:items.mcpSecurity', orgIds: [3] },
  { to: '/security-atlas/agent-instructions', icon: 'file-text', labelKey: 'nav:items.agentInstructions', orgIds: [3] },
  { to: '/security-atlas/data-security', icon: 'database', labelKey: 'nav:items.dataSecurity' },
  { to: '/security-atlas/exceptions', icon: 'check-circle', labelKey: 'nav:items.exceptions' },
];

const Sidebar = ({ isCollapsed, onToggle, onMobileClose, isMobile = false, isOpen = true }) => {
  const pinsApi = usePins();
  // Shared subgroup headings and security labels live in `nav`; loading both
  // prevents production from rendering raw `headings.*` / `items.*` keys.
  const { t } = useTranslation(['layout', 'nav']);

  const inboxUnread = useInboxUnread();
  // Code Security's own Recommendations badge: its findings only.
  const codeSecurityUnread = useInboxUnread('sast');
  // Custom Agents' own Recommendations badge: this surface's findings only.
  // The engine module id is underscored, unlike the UI product key.
  const customAgentsUnread = useInboxUnread('custom_agents');
  const location = useLocation();
  const navigate = useNavigate();
  // "New navigation" (account menu): the pillar layout in place of the Agent
  // Atlas section, Management moved to the account menu. Off by default.
  const { on: pillarNav } = usePillarNav();

  // Lens: the frame's pages and the lens's own groups first, then the modules it
  // shows whole (drawn by the same section code as the full nav), and the rest of
  // this product's navigation behind "All modules". The lens arrives filtered by
  // the user's access (config/lenses/derive.js), so nothing the nav hides shows.
  const { lens, lensId, chosenForRole } = useLens();
  // Surfaces: which module sections the sidebar shows (the multi-select that
  // replaced the product dropdown). A lens's own modules follow it too.
  const surfaces = useSurfaceSelection();
  const { isShown: surfaceShown } = surfaces;
  // The pillar layout keeps its shape: a lens (a persona) only trims its entries (renderPillarNav).
  const lensActive = lensId !== 'all' && !pillarNav;
  const lensGroups = lensActive ? lens.groups : [];
  const lensSectionKeys = useMemo(
    () => new Set(lensActive ? lens.sections.filter((key) => surfaceShown(key)) : []),
    [lensActive, lens, surfaceShown],
  );
  const [allModulesOpen, setAllModulesOpen] = useState(false);
  useEffect(() => {
    const here = location.pathname;
    const inGroups = lensGroups.some((g) => g.items.some((it) => here === it.to || here.startsWith(`${it.to}/`)));
    // A section the lens shows whole covers its own routes (isSectionActive, which
    // the headings use) and, for a product module, every route of that product.
    const product = PRODUCTS.find((p) => p.id === detectProductFromPath(here));
    const inSections = [...lensSectionKeys].some((k) => isSectionActive(k))
      || (Boolean(product) && product.sections.some((k) => lensSectionKeys.has(k)));
    setAllModulesOpen(lensActive && !inGroups && !inSections);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lensId]);

  // "Security" expander inside the Agent Atlas section. Desktop: hover shows a
  // slide-out flyout submenu (portal). Mobile: click toggles an inline list.
  // Persisted like the other subgroups (sidebar_collapsed_subgroups); only
  // falls back to "open because the current page is under it" when the user
  // has never clicked it.
  const [atlasSecurityOpen, setAtlasSecurityOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar_atlas_security_open');
    if (saved !== null) {
      try {
        return JSON.parse(saved);
      } catch {
        // fall through to the path-based default
      }
    }
    return (location.pathname || '').startsWith('/security-atlas');
  });

  // Keep LLM Cost filters (time_range, custom dates, agent) when opening Budget / Allocation from FinOps.
  const finopsPreservedSearch = useMemo(
    () => (location.pathname.startsWith('/finops') ? location.search : ''),
    [location.pathname, location.search],
  );

  // Re-rendered when another tab changes the signed-in user (the storage
  // listener below); the licence and role gates read the user afresh.
  const [, setUserRole] = useState(() => readNormalizedUserFromStorage().role_name || null);

  const [currentOrgId] = useState(() => {
    const u = readNormalizedUserFromStorage();
    const v = u.organization_id ?? u.org_id ?? u.organizationId;
    return v != null ? Number(v) : null;
  });

  // Module licence: decides the locks drawn beside unlicensed products,
  // sections and rows. Nothing is hidden for want of a licence any more —
  // the pages open on their lock screen (see components/licensing).
  const license = useModuleLicense();
  const prevPathnameRef = useRef(location.pathname);
  // Which sections/lens-groups held the *previous* page, so the auto-unfold
  // effect below can tell "just arrived here" from "still browsing around in
  // here" and only force a section open on the former.
  const prevSectionKeysRef = useRef(new Set());
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoError, setLogoError] = useState(false);
  const [isLoadingLogo, setIsLoadingLogo] = useState(true);

  // Desktop collapsed: the rail of section tiles. Which section's flyout is
  // open, and whether a click (or the keyboard) opened it — a clicked flyout
  // stays until the user picks a page, clicks away or presses Escape; a
  // hovered one closes when the pointer leaves.
  const [hoveredSection, setHoveredSection] = useState(null);
  const [flyoutPinned, setFlyoutPinned] = useState(false);

  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0, left: 0 });
  const sectionRefs = useRef({});
  const hoverTimeoutRef = useRef(null);
  const openTimeoutRef = useRef(null);
  const flyoutRef = useRef(null);
  const focusFlyoutRef = useRef(false);
  const sidebarScrollRef = useRef(null);
  const scrollSnapshotRef = useRef(0);

  // Desktop expanded: track which sections are collapsed (click to toggle)
  const [desktopCollapsed, setDesktopCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_desktop_collapsed');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // fall through to default
      }
    }
    // Nothing stored: every section starts folded (isDesktopCollapsed and
    // isMobileCollapsed read "unset" as folded); the one holding the current
    // page is unfolded on arrival.
    return {};
  });

  // Nested nav sub-groups (e.g. Agent Atlas › Responsible AI › FinOps).
  // Keyed by group label. Unlike sections these default to *open* — a group
  // only exists to reveal two or three children, and collapsing by default
  // just hides them again.
  const [collapsedSubGroups, setCollapsedSubGroups] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed_subgroups');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // fall through to default
      }
    }
    return {};
  });

  // Mobile: track collapsed sections (inline expand/collapse)
  const [mobileCollapsed, setMobileCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar_collapsed_sections');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        // fall through to default
      }
    }
    // Nothing stored: every section starts folded (isDesktopCollapsed and
    // isMobileCollapsed read "unset" as folded); the one holding the current
    // page is unfolded on arrival.
    return {};
  });

  // Keep scrollSnapshotRef up-to-date at all times so it holds the real position
  // before any route-change re-render can reset scrollTop to 0.
  useEffect(() => {
    const el = sidebarScrollRef.current;
    if (!el) return;
    const onScroll = () => { scrollSnapshotRef.current = el.scrollTop; };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Restore scroll after DOM mutations from route-change re-renders, before paint.
  // scrollSnapshotRef already holds the correct value from the scroll listener above.
  useLayoutEffect(() => {
    if (sidebarScrollRef.current && scrollSnapshotRef.current > 0) {
      sidebarScrollRef.current.scrollTop = scrollSnapshotRef.current;
    }
  }, [location.pathname]);

  // Close flyout and sidebar on route change
  useEffect(() => {
    // Close flyout on any route change
    setHoveredSection(null);
    setFlyoutPinned(false);

    if (isMobile && isOpen && onMobileClose && prevPathnameRef.current !== location.pathname) {
      const timer = setTimeout(() => {
        onMobileClose();
      }, 300);
      prevPathnameRef.current = location.pathname;
      return () => clearTimeout(timer);
    }
    prevPathnameRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Function to get user role from localStorage
  const getUserRole = () => readNormalizedUserFromStorage().role_name || null;

  useEffect(() => {
    const updateUserRole = () => {
      const role = getUserRole();
      setUserRole(role);
    };

    updateUserRole();

    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        updateUserRole();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Browsing, not hiding: every section shows; renderSection draws a lock on
  // one whose module the org has not bought.
  const showNavSection = () => true;
  // A row's lock, unless its section or sub-group already shows the module's lock.
  const navLock = (to) => <RowLock to={to} license={license} />;

  // Fetch organization logo
  useEffect(() => {
    let isMounted = true;

    const fetchLogo = async () => {
      console.log('[Sidebar Logo] Starting logo fetch...');
      console.log('[Sidebar Logo] localStorage user:', localStorage.getItem('user'));
      try {
        setIsLoadingLogo(true);
        setLogoError(false);

        const url = await organizationService.getCurrentOrganizationLogo();
        console.log('[Sidebar Logo] getCurrentOrganizationLogo() returned:', url);

        if (isMounted) {
          console.log('[Sidebar Logo] Setting logoUrl state to:', url);
          setLogoUrl(url);
          setIsLoadingLogo(false);
        } else {
          console.log('[Sidebar Logo] Component unmounted before logo arrived — not setting state');
        }
      } catch (error) {
        console.error('[Sidebar Logo] Logo fetch threw an error:', error);
        if (isMounted) {
          setIsLoadingLogo(false);
          setLogoError(true);
        }
      }
    };

    fetchLogo();

    const handleStorageChange = (e) => {
      if (e.key === 'user' && isMounted) {
        console.log('[Sidebar Logo] user changed in localStorage — refetching logo');
        fetchLogo();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const handleImageError = (e) => {
    console.error('[Sidebar Logo] <img> failed to load! src was:', e.target?.src);
    setLogoError(true);
  };

  const showTextLogo = !logoUrl || logoError || isLoadingLogo;
  // Collapsed, the header draws the brand (HeaderBrand), so the rail has no
  // logo of its own: its old "S" mark only repeated it.
  const brandInHeader = !isMobile && isCollapsed;
  console.log('[Sidebar Logo] Render state → logoUrl:', logoUrl, '| logoError:', logoError, '| isLoadingLogo:', isLoadingLogo, '| showTextLogo:', showTextLogo);

  const handleToggle = (e) => {
    e.preventDefault();
    if (onToggle) {
      onToggle();
    }
  };

  // Mobile: toggle section collapse
  const toggleMobileSection = (sectionKey, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setMobileCollapsed(prev => {
      // Unset reads as folded (open for a pillar), so a first click flips that.
      const newState = { ...prev, [sectionKey]: !isFolded(prev, sectionKey) };
      localStorage.setItem('sidebar_collapsed_sections', JSON.stringify(newState));
      return newState;
    });
  };

  // Folded unless the user opened it (or it holds the page, see below).
  const isMobileCollapsed = (sectionKey) => isFolded(mobileCollapsed, sectionKey);

  // Desktop expanded: toggle section collapse (click to expand/collapse)
  const toggleDesktopSection = (sectionKey, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setDesktopCollapsed(prev => {
      const newState = { ...prev, [sectionKey]: !isFolded(prev, sectionKey) };
      localStorage.setItem('sidebar_desktop_collapsed', JSON.stringify(newState));
      return newState;
    });
  };

  const isDesktopCollapsed = (sectionKey) => isFolded(desktopCollapsed, sectionKey);

  const toggleSubGroup = (key, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCollapsedSubGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('sidebar_collapsed_subgroups', JSON.stringify(next));
      return next;
    });
  };

  const isActive = (requestedPath) => {
    // A canonical /custom-agents URL highlights the row of the page it mirrors.
    const pathname = toLegacyPath(location.pathname);
    const path = toLegacyPath(String(requestedPath).split('?')[0]);

    // `/endpoint-security` is both the Overview page and the prefix of every other page
    // in the module, so the generic prefix match below kept Overview highlighted
    // on all of them. Exact-match it, the same way /observability and /dashboard
    // are handled.
    if (path === '/endpoint-security') {
      return pathname === '/endpoint-security' || pathname === '/endpoint-security/';
    }
    // Assessment's Overview is the prefix of its other three pages: exact-match it.
    if (path === '/agent-atlas/assessment' || path === '/custom-agents/assessment') {
      return pathname === path || pathname === `${path}/`;
    }
    if (path === '/dashboard') {
      return pathname === '/dashboard';
    }

    if (path === '/finops/simulator') {
      return pathname === '/finops/simulator' || pathname.startsWith('/finops/simulator/');
    }
    if (path === '/finops/usage-roi') {
      // Tokenomics is a tab of Usage & ROI and /finops lands on it, so the row
      // also owns those paths (each redirects here).
      return (
        pathname === '/finops/usage-roi'
        || pathname.startsWith('/finops/usage-roi/')
        || pathname === '/finops/roi'
        || pathname === '/finops/tokenomics'
        || pathname.startsWith('/finops/tokenomics/')
        || pathname === '/finops'
        || pathname === '/finops/'
      );
    }
    if (path === '/finops/budget') {
      return pathname === '/finops/budget' || pathname.startsWith('/finops/budget/');
    }
    if (path === '/finops/allocation') {
      return pathname === '/finops/allocation' || pathname.startsWith('/finops/allocation/');
    }
    if (path === '/finops/greenops') {
      return pathname === '/finops/greenops' || pathname.startsWith('/finops/greenops/');
    }
    if (path === '/finops/settings') {
      return pathname === '/finops/settings' || pathname.startsWith('/finops/settings/');
    }
    if (path === '/finops') {
      return pathname === '/finops' || pathname === '/finops/';
    }
    // AI Gateway overview is the canonical /admin/ai-gateway entry; do
    // exact-match so it doesn't stay highlighted on the sub-pages.
    if (path === '/admin/ai-gateway') {
      return pathname === '/admin/ai-gateway' || pathname === '/admin/ai-gateway/';
    }

    // Observability is its own page AND a prefix of /observability/alerts, so
    // exact-match it — otherwise it stays highlighted on the Alert Settings page.
    if (path === '/observability') {
      return pathname === '/observability' || pathname === '/observability/';
    }

    if (path.startsWith('/admin/')) {
      return pathname === path || pathname.startsWith(`${path}/`);
    }

    return pathname === path || pathname.startsWith(`${path}/`);
  };

  // Management access is based on the full authenticated user, not only the
  // first role returned by the API. A user can have a module-scoped role in
  // `role_name` while also holding a global Administrator role in `roles`.
  const isAdministrator = isPrivilegedUser(readNormalizedUserFromStorage());

  const canManageEndpointSettings = canManageEndpointPolicy();

  const sidebarClasses = [
    'sidebar',
    isMobile ? 'mobile' : '',
    isMobile && isOpen ? 'mobile-open' : '',
    !isMobile && isCollapsed ? 'collapsed' : '',
  ].filter(Boolean).join(' ');

  // Map section keys to route prefixes for active-section highlighting
  const sectionRoutes = {
    'agent-atlas': ['/agent-atlas/'],
    'cloud-ai': ['/cloud/'],
    'custom-agents': [
      '/custom-agents', '/observability', '/agent-network', '/conversation-explorer',
      '/user-behavior', '/intent-analysis', '/global-search', '/security/', '/reports',
      '/admin/compliance', '/admin/audit-report', '/admin/custom-guardrails',
      '/admin/policies', '/admin/violations', '/admin/policy-observability',
      '/admin/policy-simulation', '/admin/policy-audit-log', '/finops',
      '/model-evaluation', '/red-team-scanner', '/red-team/scheduled-scans',
    ],
    'action-center': ['/action-center'],
    'monitoring': ['/agent-network', '/conversation-explorer', '/user-behavior', '/observability', '/global-search'],
    'security': ['/security/', '/admin/compliance', '/admin/audit-report', '/admin/custom-guardrails'],
    'finops': ['/finops', '/finops/tokenomics', '/finops/usage-roi'],
    'model-selection': ['/model-evaluation', '/red-team-scanner', '/red-team/scheduled-scans'],
    'governance': ['/dashboard', '/admin/policies', '/admin/violations', '/admin/policy-observability', '/admin/agent-behavior', '/admin/policy-simulation', '/admin/policy-audit-log'],
    'shadow-ai': ['/endpoint-security', '/endpoint-security/endpoints', '/endpoint-security/users',
      '/endpoint-security/log-explorer', '/endpoint-security/dlp-analytics', '/endpoint-security/guardrails', '/endpoint-security/alerts',
      '/endpoint-security/policy', '/endpoint-security/compliance', '/endpoint-security/audit-report', '/endpoint-security/tool-catalog',
      '/endpoint-security/coverage', '/endpoint-security/adoption'],
    'endpoint-management': ['/admin/fleet-overview', '/admin/shadow-ai-discovery', '/admin/ai-conversations', '/admin/dlp-analytics', '/admin/endpoint-violations', '/admin/endpoint-user-activity', '/admin/ai-tool-catalog', '/admin/endpoint-alerts', '/admin/endpoint-settings'],
    'mdm': ['/admin/mdm/overview', '/admin/mdm/devices', '/admin/mdm/software', '/admin/mdm/mcp', '/admin/mdm/policy'],
    'edr': [
      '/admin/edr/overview',
      '/admin/edr/endpoints',
      '/admin/edr/processes',
      '/admin/edr/extensions',
      '/admin/edr/mcp',
      '/admin/edr/alerts',
    ],
    'ai-gateway': ['/admin/ai-gateway', '/admin/ai-gateway/events', '/admin/ai-gateway/configuration', '/admin/ai-gateway/policy', '/admin/ai-gateway/keys', '/admin/ai-gateway/usage'],
    'enterprise-ai': ['/copilot/dashboard', '/copilot/recommendations', '/copilot/agent-network', '/copilot/registry', '/copilot/usage-roi', '/copilot/users', '/copilot/applications', '/copilot/knowledge-gaps', '/copilot/intent-analysis', '/copilot/conversations', '/copilot/threats', '/copilot/posture', '/copilot/sources', '/copilot/security/', '/copilot/log-explorer', '/copilot/audit-log', '/copilot/compliance', '/copilot/audit-report', '/copilot/alerts', '/copilot/settings'],
    'copilot-studio': ['/copilot/studio/'],
    'gemini-workspace': ['/gemini/dashboard', '/gemini/threats', '/gemini/security/', '/gemini/users', '/gemini/applications', '/gemini/intent-analysis', '/gemini/usage-roi', '/gemini/log-explorer', '/gemini/audit-log', '/gemini/compliance', '/gemini/audit-report', '/gemini/alerts', '/gemini/settings'],
    'gemini-enterprise-agents': ['/gemini/agents/'],
    'saas-ai-platform': ['/saas-ai'],
    'sast': ['/sast/overview', '/sast/recommendations', '/sast/repositories', '/sast/findings', '/sast/scans/', '/sast/settings', '/sast/policy', '/sast/compliance', '/sast/security/'],
    'management': ['/security-atlas/automation', '/admin/agents', '/admin/teams', '/admin/users', '/admin/custom-roles', '/admin/alert-configuration', '/admin/recommendation-rules', '/admin/sso', '/admin/api-keys', '/admin/integrations', '/admin/audit-log'],
    'custom-dashboards': ['/ai-explorer/dashboards'],
    ...Object.fromEntries(PILLARS.map((p) => [p.key, pillarRoutes(p)])),
  };

  const isSectionActive = (sectionKey) => {
    const routes = sectionRoutes[sectionKey];
    if (!routes) return false;
    const pathname = toLegacyPath(location.pathname);
    return routes.some((route) => pathname === route || pathname.startsWith(route));
  };

  // Does this section hold the current page? Atlas's Security pages live under
  // /security-atlas, outside the Agent Atlas prefix.
  const sectionHoldsPath = (sectionKey) => isSectionActive(sectionKey)
    || (sectionKey === 'agent-atlas' && location.pathname.startsWith('/security-atlas'));

  // Sections start folded; arriving on a page unfolds the section that holds it
  // (and a lens group listing it), so where you are is always in view. The user
  // can fold it again; that choice is remembered like any other — including
  // while navigating between pages inside that same section: this only forces
  // a section open the moment it is first entered (it wasn't already current
  // on the previous page). Without that guard, every click to another page in
  // a section the user had just folded snapped it back open, which read as
  // the sidebar expanding and collapsing on its own.
  useEffect(() => {
    const here = location.pathname;
    const keys = [
      ...SIDEBAR_SECTION_KEYS.filter(sectionHoldsPath),
      ...lensGroups
        .filter((g) => g.items.some((it) => here === it.to || here.startsWith(`${it.to}/`)))
        .map((g) => `lens-${lens.id}-${g.key}`),
    ];
    const prevKeys = prevSectionKeysRef.current;
    prevSectionKeysRef.current = new Set(keys);
    const newlyEntered = keys.filter((k) => !prevKeys.has(k));
    if (newlyEntered.length === 0) return;
    const unfold = (storageKey) => (prev) => {
      if (newlyEntered.every((k) => prev[k] === false)) return prev;
      const next = { ...prev };
      newlyEntered.forEach((k) => { next[k] = false; });
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    };
    setDesktopCollapsed(unfold('sidebar_desktop_collapsed'));
    setMobileCollapsed(unfold('sidebar_collapsed_sections'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, lensId]);

  const openFlyout = (sectionKey) => {
    setHoveredSection(sectionKey);
    const el = sectionRefs.current[sectionKey];
    if (el) {
      const rect = el.getBoundingClientRect();
      const sidebarEl = el.closest('.sidebar');
      const sidebarRect = sidebarEl ? sidebarEl.getBoundingClientRect() : rect;
      setFlyoutPosition({
        top: rect.top,
        left: sidebarRect.right,
      });
    }
  };

  const closeFlyout = () => {
    setHoveredSection(null);
    setFlyoutPinned(false);
  };

  // Hover intent: a short pause before the first flyout opens, so sweeping the
  // pointer across the rail doesn't flash menus; once one is open, the next
  // opens at once. `immediate` is the flyout itself being re-entered.
  const handleMouseEnter = (sectionKey, immediate = false) => {
    clearTimeout(hoverTimeoutRef.current);
    clearTimeout(openTimeoutRef.current);
    if (hoveredSection === sectionKey) return;
    if (immediate || hoveredSection) {
      setFlyoutPinned(false);
      openFlyout(sectionKey);
      return;
    }
    openTimeoutRef.current = setTimeout(() => openFlyout(sectionKey), 90);
  };

  // Long enough to cross diagonally from the tile into its flyout.
  const handleMouseLeave = () => {
    clearTimeout(openTimeoutRef.current);
    if (flyoutPinned) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredSection(null);
    }, 250);
  };

  // Click or Enter/Space on a tile: keep its flyout open; again closes it.
  const toggleFlyout = (sectionKey, fromKeyboard = false) => {
    clearTimeout(openTimeoutRef.current);
    clearTimeout(hoverTimeoutRef.current);
    if (hoveredSection === sectionKey && flyoutPinned) {
      closeFlyout();
      return;
    }
    focusFlyoutRef.current = fromKeyboard;
    openFlyout(sectionKey);
    setFlyoutPinned(true);
  };

  const closeFlyoutToTile = () => {
    const tile = hoveredSection && sectionRefs.current[hoveredSection];
    closeFlyout();
    const heading = tile && tile.querySelector('.sidebar-heading');
    if (heading) heading.focus();
  };

  // Keep the flyout on screen: a tile low in the rail would open it past the
  // bottom edge. Then hand focus to its first link when the keyboard opened it.
  useLayoutEffect(() => {
    const el = flyoutRef.current;
    if (!hoveredSection || !el) return;
    const maxTop = window.innerHeight - el.offsetHeight - 8;
    if (flyoutPosition.top > maxTop) {
      setFlyoutPosition((p) => ({ ...p, top: Math.max(8, maxTop) }));
    }
    if (focusFlyoutRef.current) {
      focusFlyoutRef.current = false;
      const first = el.querySelector('a, button');
      if (first) first.focus();
    }
  }, [hoveredSection, flyoutPosition.top]);

  // A clicked flyout closes on a click anywhere outside it and its tile.
  useEffect(() => {
    if (!flyoutPinned || !hoveredSection) return undefined;
    const onDown = (e) => {
      if (flyoutRef.current && flyoutRef.current.contains(e.target)) return;
      const tile = sectionRefs.current[hoveredSection];
      if (tile && tile.contains(e.target)) return;
      closeFlyout();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [flyoutPinned, hoveredSection]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      clearTimeout(hoverTimeoutRef.current);
      clearTimeout(openTimeoutRef.current);
    };
  }, []);

  // Helper: an expandable group of links nested one level inside a section
  // (e.g. Agent Atlas › Responsible AI › FinOps). Renders as an <li> so it can
  // sit among the section's ordinary <li> links.
  //
  // The toggle row is styled as a peer of the nav links around it rather than
  // as a third tier of all-caps heading — the section heading already owns
  // that treatment, and repeating it made the sub-groups read as new sections
  // instead of children. Depth is carried by indent + a guide rail instead.
  // One link at section level (not inside a subgroup) — Notifications, which sits on its
  // own before Security in every module.
  const renderNavLink = (item) => (
    <li key={item.to}>
      <Link to={item.to} className={`nav-link ${(item.active ?? isActive(item.to)) ? 'active' : ''}`}>
        <FeatherIcon name={item.icon} size={18} className="nav-icon" />
        <span>{item.label}</span>
        {navLock(item.to)}
        {/* Rendered only above zero: null means the count is unknown, and a
            confident 0 next to a service that could not answer is the badge
            lying about the estate. See useInboxUnread. */}
        {item.badge > 0 && (
          <span className="nav-badge" title={`${item.badge} unread`}>
            {item.badge > 99 ? '99+' : item.badge}
          </span>
        )}
      </Link>
    </li>
  );

  // `key` is a stable id (section + group), independent of the translated
  // `label` — several sections reuse the same label (e.g. every module's
  // "Security" group), and keying the open/closed state by label collapsed
  // them all together: toggling one module's Security group silently
  // toggled every other module's Security group that shared the storage key.
  const renderSubGroup = (key, label, icon, items) => {
    const open = collapsedSubGroups[key] !== true;
    const childActive = items.some((item) => isActive(item.to));
    // Every row locked: one lock on the group, none on its rows.
    const groupLocked = items.length > 0 && items.every((item) => isNavPathLocked(item.to, license));
    return (
      <li className={`nav-subgroup ${open ? 'is-open' : ''}`} key={key}>
        <button
          type="button"
          className={`nav-subgroup-toggle ${childActive ? 'has-active' : ''}`}
          onClick={(e) => toggleSubGroup(key, e)}
          aria-expanded={open}
        >
          <FeatherIcon name={icon} size={18} className="nav-icon" />
          <span>{label}</span>
          <ContainerLock locked={groupLocked} module={groupLocked ? moduleLabelFor(items[0].to, license) : ''} />
          <FeatherIcon
            name={open ? 'chevron-down' : 'chevron-right'}
            className="nav-subgroup-caret"
          />
        </button>
        {open && (
          <LockedParent locked={groupLocked}>
          <ul className="nav-subgroup-items">
            {items.map((item) => (
              <li key={item.to}>
                <Link to={item.to} className={`nav-link ${isActive(item.to) ? 'active' : ''}`}>
                  <FeatherIcon name={item.icon} size={18} className="nav-icon" />
                  <span>{item.label}</span>
                  {navLock(item.to)}
                </Link>
              </li>
            ))}
          </ul>
          </LockedParent>
        )}
      </li>
    );
  };

  // Compliance is drawn the same way in Atlas and every module (user, 2026-10-20): a Compliance
  // group holding the module's Compliance page and its Audit Report (the Atlas point-in-time
  // report pinned to the module), then Log Explorer as its own link right below it — the logs
  // are every event the module recorded, not a compliance page — then Policy and Notifications.
  // A module with no audit report yet keeps the group's shape with its one page.
  const renderComplianceGroup = (key, { compliance, auditReport = null }) => renderSubGroup(key, t('nav:headings.compliance'), 'clipboard', [
    { to: compliance, icon: 'shield', label: t('nav:items.compliance') },
    ...(auditReport ? [{ to: auditReport, icon: 'file-text', label: t('nav:items.auditReport') }] : []),
  ]);
  const renderLogExplorer = (to) => renderNavLink({ to, icon: 'list', label: t('nav:items.logExplorer') });

  const renderGeminiStyleNav = (sectionKey, {
    overview, adoption, security, notifications, policy, compliance, auditReport, logExplorer, settings,
  }) => {
    const localize = (item) => localizeGeminiNavItem(item, t);
    const renderLink = (item) => (
      <li key={item.to}>
        <Link to={item.to} className={`nav-link ${isActive(item.to) ? 'active' : ''}`}>
          <FeatherIcon name={item.labelKey === 'agentRegistry' ? 'server' : item.icon} size={18} className="nav-icon" />
          <span>{localize(item).label}</span>
          {navLock(item.to)}
        </Link>
      </li>
    );

    return <>
      {overview.map(renderLink)}
      {adoption.length > 0 && renderSubGroup(`${sectionKey}.adoption`, t('nav:headings.adoption'), 'trending-up', adoption.map(localize))}
      {renderSubGroup(`${sectionKey}.security`, t('nav:headings.security'), 'shield', security.map(localize))}
      {renderComplianceGroup(`${sectionKey}.compliance`, { compliance: compliance.to, auditReport: auditReport ? auditReport.to : null })}
      {renderLogExplorer(logExplorer.to)}
      {policy && renderLink(policy)}
      {/* Notifications sit under Policy in every module (2026-10-03). */}
      {notifications && renderLink(notifications)}
      {settings && renderLink(settings)}
    </>;
  };

  const renderMicrosoftNav = (studio) => renderGeminiStyleNav(studio ? 'copilot-studio' : 'enterprise-ai', getCopilotNavGroups(studio));

  // Helper: render a sidebar section
  // - Mobile: inline expand/collapse on click
  // - Desktop expanded: inline expand/collapse on click (normal sidebar)
  // - Desktop collapsed: hover flyout via portal, or, with `link`, the tile
  //   opens that page directly (a one-page section needs no submenu)
  const renderSection = (sectionKey, title, icon, navItems, options = {}) => {
    const { adminOnly, link, module } = options;
    // A pillar names its licence module outright (pillarNavConfig `module`); a classic
    // section is looked up by key in the catalog's ui_sections.
    const sectionLocked = module ? !license.isLicensed(module) : !license.isSectionLicensed(sectionKey);
    const lockModule = module ? license.moduleLabel(module) : title;

    const isDesktopExpanded = !isMobile && !isCollapsed;
    const isDesktopCollapsedMode = !isMobile && isCollapsed;

    // Determine if this section's nav items should be hidden
    const sectionHidden = isMobile
      ? isMobileCollapsed(sectionKey)
      : isDesktopExpanded
        ? isDesktopCollapsed(sectionKey)
        : true; // desktop collapsed: always hide inline, use flyout

    const headingProps = {};
    if (isMobile) {
      headingProps.onClick = (e) => toggleMobileSection(sectionKey, e);
    } else if (isDesktopExpanded) {
      headingProps.onClick = (e) => toggleDesktopSection(sectionKey, e);
      headingProps.style = { cursor: 'pointer' };
    }

    const directLink = isDesktopCollapsedMode && Boolean(link);
    const showFlyout = isDesktopCollapsedMode && !directLink && hoveredSection === sectionKey;

    // Collapsed: the heading is the section's tile — icon over its name — and
    // opens the section as a flyout, by hover, click or keyboard.
    if (directLink) {
      headingProps.role = 'link';
      headingProps.tabIndex = 0;
      headingProps.onClick = () => navigate(link);
      headingProps.onKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          navigate(link);
        }
      };
    } else if (isDesktopCollapsedMode) {
      headingProps.role = 'button';
      headingProps.tabIndex = 0;
      headingProps['aria-haspopup'] = 'true';
      headingProps['aria-expanded'] = showFlyout;
      headingProps.onClick = () => toggleFlyout(sectionKey);
      headingProps.onKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleFlyout(sectionKey, true);
        } else if (e.key === 'Escape' && showFlyout) {
          closeFlyout();
        }
      };
    }
    const navClass = `sidebar-nav ${sectionHidden ? 'section-collapsed' : ''}`;

    const section = (
      <div
        className="sidebar-section"
        ref={(el) => { sectionRefs.current[sectionKey] = el; }}
        onMouseEnter={isDesktopCollapsedMode && !directLink ? () => handleMouseEnter(sectionKey) : undefined}
        onMouseLeave={isDesktopCollapsedMode && !directLink ? handleMouseLeave : undefined}
      >
        <h6 className={`sidebar-heading ${isSectionActive(sectionKey) ? 'active' : ''}`} {...headingProps}>
          {/* Decorative: the title beside it already names the product. Its own
              class, because the .section-icon rules recolour and stroke the
              glyph (dark rail forces a stroke), which would outline the mark. */}
          {SECTION_BRAND_SURFACE[sectionKey] ? (
            <span className="section-tile is-brand" aria-hidden="true">
              <SurfaceIcon
                surface={SECTION_BRAND_SURFACE[sectionKey]}
                size={isDesktopCollapsedMode ? 18 : 14}
                className="section-brand-icon"
                decorative
              />
            </span>
          ) : SECTION_COLOR[sectionKey] ? (
            <span className="section-tile" style={{ '--section-c': SECTION_COLOR[sectionKey] }} aria-hidden="true">
              <FeatherIcon name={icon} size={isDesktopCollapsedMode ? 16 : 13} className="section-color-icon" />
            </span>
          ) : (
            <FeatherIcon name={icon} size={isDesktopCollapsedMode ? 20 : 16} className="section-icon" />
          )}
          <span>{title}</span>
          {sectionLocked && <NavLock module={lockModule} className={isDesktopCollapsedMode ? 'rail-lock' : ''} />}
          {(isMobile || isDesktopExpanded) && (
            <FeatherIcon
              name={sectionHidden ? 'chevron-right' : 'chevron-down'}
              className="section-toggle-icon"
            />
          )}
        </h6>
        {/* Inline nav (mobile + desktop expanded) */}
        <ul className={navClass}>
          <LockedParent locked={sectionLocked}>{navItems}</LockedParent>
        </ul>
        {/* Desktop collapsed: flyout panel rendered via portal */}
        {showFlyout && ReactDOM.createPortal(
          <div
            ref={flyoutRef}
            className="sidebar-flyout"
            style={{ top: flyoutPosition.top, left: flyoutPosition.left }}
            onMouseEnter={() => handleMouseEnter(sectionKey, true)}
            onMouseLeave={handleMouseLeave}
            onKeyDown={(e) => { if (e.key === 'Escape') closeFlyoutToTile(); }}
          >
            <div className="sidebar-flyout-header">{title}{sectionLocked && <NavLock module={lockModule} />}</div>
            <ul className="sidebar-nav">
              <LockedParent locked={sectionLocked}>{navItems}</LockedParent>
            </ul>
          </div>,
          document.body,
        )}
      </div>
    );

    if (adminOnly) {
      return isAdministrator ? section : null;
    }
    return section;
  };

  const monitoringNavItems = (
    <>
      {/* The Custom Agents Dashboard is the module overview; Observability is
          its own row next to Alerts. The legacy /dashboard page is retired. */}
      <li>
        <Link to={CUSTOM_AGENTS_DASHBOARD_PATH} className={`nav-link ${isActive(CUSTOM_AGENTS_DASHBOARD_PATH) ? 'active' : ''}`}>
          <FeatherIcon name="grid" size={18} className="nav-icon" />
          <span>{t('layout:sidebarSidebar.dashboard')}</span>
        </Link>
      </li>
      <li>
        <Link to="/conversation-explorer" className={`nav-link ${isActive('/conversation-explorer') ? 'active' : ''}`}>
          <FeatherIcon name="git-branch" size={18} className="nav-icon" />
          <span>{t('layout:sidebarSidebar.conversations')}</span>
        </Link>
      </li>
      <li>
        <Link to="/user-behavior" className={`nav-link ${isActive('/user-behavior') ? 'active' : ''}`}>
          <FeatherIcon name="alert-circle" size={18} className="nav-icon" />
          <span>{t('layout:sidebarSidebar.users')}</span>
        </Link>
      </li>
      <li>
        <Link to="/agent-network" className={`nav-link ${isActive('/agent-network') ? 'active' : ''}`}>
          <FeatherIcon name="box" size={18} className="nav-icon" />
          <span>{t('layout:sidebarSidebar.agentNetwork')}</span>
        </Link>
      </li>
      {/* Hidden until the Custom Agents intent-analysis backend is validated. */}
      {hasCapability('intentAnalysis') && (
        <li>
          <Link to="/intent-analysis" className={`nav-link ${isActive('/intent-analysis') ? 'active' : ''}`}>
            <FeatherIcon name="compass" size={18} className="nav-icon" />
            <span>{t('layout:sidebarSidebar.intentAnalysis')}</span>
          </Link>
        </li>
      )}
      <li>
        <Link to="/global-search" className={`nav-link ${isActive('/global-search') ? 'active' : ''}`}>
          <FeatherIcon name="search" size={18} className="nav-icon" />
          <span>{t('layout:sidebarSidebar.logsExplorer')}</span>
        </Link>
      </li>
      <li>
        <Link to="/observability" className={`nav-link ${isActive('/observability') ? 'active' : ''}`}>
          <FeatherIcon name="activity" size={18} className="nav-icon" />
          <span>{t('layout:sidebarSidebar.observability')}</span>
        </Link>
      </li>
      <li>
        <Link to="/observability/alerts" className={`nav-link ${isActive('/observability/alerts') ? 'active' : ''}`}>
          <FeatherIcon name="bell" size={18} className="nav-icon" />
          <span>{t('layout:sidebarSidebar.alerts')}</span>
        </Link>
      </li>
    </>
  );

  // Rows are no longer hidden for want of a licence: each shows, with a lock
  // when its page's module is not licensed (navLock). The per-module
  // conditions below are kept so the grouping reads as before.
  const canOpenCustomModule = () => true;
  const canOpenCustomGovernance = canOpenCustomModule('monitoring') || canOpenCustomModule('compliance');
  const canOpenCustomAlerts = canOpenCustomModule('monitoring');

  // Custom Agents top-level order, set with the user 2026-10-04.
  const customAgentsNavItems = (
    <>
      {canOpenCustomModule('monitoring') && renderNavLink({ to: CUSTOM_AGENTS_DASHBOARD_PATH, icon: 'grid', label: t('layout:sidebarSidebar.dashboard') })}
      {/* Second, directly under the Dashboard: the worklist is where the day
          starts, and everything below it is somewhere to look something up. */}
      {hasCapability('recommendations')
        && (canOpenCustomModule('monitoring') || canOpenCustomModule('security'))
        && renderNavLink({ to: '/custom-agents/recommendations', icon: 'inbox', label: t('nav:items.recommendations'), badge: customAgentsUnread })}
      {(isAdministrator || canOpenCustomGovernance) && renderNavLink({ to: '/custom-agents/registry', icon: 'list', label: t('layout:sidebarSidebar.agentRegistry') })}
      {canOpenCustomModule('monitoring') && renderNavLink({ to: '/user-behavior', icon: 'users', label: t('layout:sidebarSidebar.users') })}
      {canOpenCustomModule('monitoring') && renderNavLink({ to: '/agent-network', icon: 'share-2', label: t('layout:sidebarSidebar.agentNetwork') })}
      {/* Adoption also carries Source Intelligence, so its gate includes
          `security` -- otherwise that row renders with no group around it. */}
      {(canOpenCustomModule('monitoring') || canOpenCustomModule('security'))
        && renderSubGroup('custom-agents.adoption', t('nav:headings.adoption'), 'trending-up', [
          ...(canOpenCustomModule('monitoring') ? [{ to: '/custom-agents/conversations', icon: 'message-circle', label: t('layout:sidebarSidebar.conversations') }] : []),
          ...(canOpenCustomModule('monitoring') ? [{ to: '/custom-agents/knowledge-gaps', icon: 'help-circle', label: t('layout:sidebarSidebar.knowledgeGaps') }] : []),
          ...(canOpenCustomModule('monitoring') && hasCapability('intentAnalysis') ? [{ to: '/intent-analysis', icon: 'target', label: t('layout:sidebarSidebar.intentAnalysis') }] : []),
          ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/sources', icon: 'database', label: t('layout:sidebarSidebar.sourceIntelligence') }] : []),
        ])}
      {canOpenCustomGovernance && renderSubGroup('custom-agents.assessment', t('nav:sections.assessment'), 'check-square', [
        { to: '/custom-agents/assessment', icon: 'grid', label: t('nav:items.overview') },
        { to: '/red-team-scanner', icon: 'shield', label: t('layout:sidebarSidebar.redTeamAudit') },
        { to: '/red-team/scheduled-scans', icon: 'clock', label: t('layout:sidebarSidebar.scheduledScans') },
        { to: '/model-evaluation/projects', icon: 'box', label: t('layout:sidebarSidebar.modelEvaluation') },
      ])}
      {/* Gated on security/monitoring. It read
          `canOpenCustomAlerts` (monitoring OR security OR finops), so a
          finops-only tenant got the heading with no rows under it. */}
      {(canOpenCustomModule('security') || canOpenCustomModule('monitoring')) && renderSubGroup('custom-agents.security', t('nav:headings.security'), 'shield', [
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/overview', icon: 'shield', label: t('nav:items.overview') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/signals', icon: 'activity', label: t('nav:items.signals') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/hunt', icon: 'search', label: t('nav:items.hunt') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/incidents', icon: 'alert-octagon', label: t('nav:items.incidents') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/skills', icon: 'layers', label: t('nav:items.skills') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/response', icon: 'zap', label: t('nav:items.response') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/ai-bom', icon: 'package', label: t('nav:items.aiBomInventory') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/data-security', icon: 'database', label: t('layout:sidebarSidebar.dataSecurity') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/custom-agents/security/exceptions', icon: 'slash', label: t('nav:items.exceptions') }] : []),
        ...(canOpenCustomModule('security') ? [{ to: '/admin/custom-guardrails', icon: 'sliders', label: t('layout:sidebarSidebar.customGuardrails') }] : []),
      ])}
      {canOpenCustomModule('compliance') && renderComplianceGroup('custom-agents.compliance', { compliance: '/admin/compliance', auditReport: '/admin/audit-report' })}
      {canOpenCustomModule('security') && renderLogExplorer('/security/audit-log')}
      {/* After Compliance, before Observability, as its own top-level link
          rather than inside Adoption (set with the user 2026-10-04, keeping the
          2026-10-03 decision to promote it out). */}
      {canOpenCustomModule('finops') && renderNavLink({ to: '/custom-agents/finops', icon: 'dollar-sign', label: t('nav:items.finops') })}
      {canOpenCustomModule('monitoring') && renderNavLink({ to: '/observability', icon: 'activity', label: t('layout:sidebarSidebar.observability') })}
      {(canOpenCustomGovernance || canOpenCustomModule('security')) && renderNavLink({ to: '/admin/policies', icon: 'shield', label: t('nav:items.policy') })}
      {canOpenCustomAlerts && renderNavLink({ to: '/observability/alerts', icon: 'bell', label: t('nav:items.notifications') })}
      {/* No Settings row: Custom Agents has no settings page of its own (2026-10-04). */}
    </>
  );

  // The pillar layout ("New navigation"): Home, Inbox, Log Explorer and Notifications,
  // then five pillars, in place of the Agent Atlas section. Same pages, gates and locks
  // as the classic rows. An entry with a tab group stays lit on every tab of it
  // (pillarTabs.js); a `headingKey` row is a sub-heading (Security › Posture).
  const pillarGates = { canGovern: canOpenCustomGovernance, orgId: currentOrgId, featureOn: isFeatureEnabled };
  const hereTab = tabFor(location.pathname, location.search);
  const pillarLink = (item) => renderNavLink({
    to: item.to,
    icon: item.icon,
    label: t(`nav:${item.labelKey}`),
    badge: item === PILLAR_INBOX ? inboxUnread : null,
    active: item.group ? hereTab?.group === item.group || isActive(item.to) : undefined,
  });
  // A persona keeps the entries that hold its pages (lensKeepsItem) and drops a pillar left with
  // none; Everything shows them all. The entry holding the page you are on stays, so where you
  // are is always in view (the classic lens opens "All modules" for the same reason).
  const pillarHere = (item) => (item.group ? hereTab?.group === item.group || isActive(item.to) : isActive(item.to));
  const pillarLens = pillarNav && lensId !== 'all' ? {
    pages: new Set(lens.groups.flatMap((g) => g.items.map((it) => String(it.to).split('?')[0]))),
    sections: lens.sections,
    sectionRoutes: (key) => sectionRoutes[key],
  } : null;
  const shownPillars = pillarLens
    ? trimPillars(PILLARS, (it) => passesGates(it, pillarGates) && (lensKeepsItem(it, pillarLens) || pillarHere(it)))
    : PILLARS;
  const renderPillarItem = (item) => {
    if (item.headingKey) {
      return <li key={item.headingKey} className="pillar-subheading">{t(`nav:${item.headingKey}`)}</li>;
    }
    return passesGates(item, pillarGates) ? pillarLink(item) : null;
  };
  // A top row is a tile like a pillar heading: its icon in its colour on a tint of it, and in
  // the folded rail the caption sits under the tile (Sidebar.css `.pillar-top`).
  const renderTopLink = (item) => {
    const active = item.group ? hereTab?.group === item.group || isActive(item.to) : isActive(item.to);
    const badge = item === PILLAR_INBOX ? inboxUnread : null;
    return (
      <li key={item.to}>
        <Link to={item.to} className={`nav-link pillar-top-link ${active ? 'active' : ''}`}>
          <span className="pillar-top-tile" aria-hidden="true" style={{ '--section-c': item.color }}>
            <FeatherIcon name={item.icon} size={isCollapsed && !isMobile ? 16 : 13} className="pillar-top-icon" />
          </span>
          <span className="pillar-top-label">{t(`nav:${item.labelKey}`)}</span>
          {navLock(item.to)}
          {/* Null, not 0, when the count is unknown — see useInboxUnread. */}
          {badge > 0 && (
            <span className="nav-badge" title={`${badge} unread`}>{badge > 99 ? '99+' : badge}</span>
          )}
        </Link>
      </li>
    );
  };
  const renderPillarNav = () => (
    <>
      <div className="pillar-top">
        <ul className="sidebar-nav">
          {PILLAR_TOP.filter((it) => passesGates(it, pillarGates)).map(renderTopLink)}
        </ul>
      </div>
      <div className="pillar-sections">
        {shownPillars.map((p) => (
          <React.Fragment key={p.key}>
            {/* One lock, on the heading, when the org has not bought the pillar's module; its
                rows then leave the lock to it (a core row such as Policy never locks). */}
            {renderSection(p.key, t(`nav:${p.labelKey}`), p.icon, <>{p.items.map(renderPillarItem)}</>, { module: p.module })}
          </React.Fragment>
        ))}
      </div>
      <div className="pillar-top pillar-bottom">
        <ul className="sidebar-nav">
          {PILLAR_BOTTOM.filter((it) => passesGates(it, pillarGates)).map(renderTopLink)}
        </ul>
      </div>
    </>
  );

  // Every module section and the always-visible ones, drawn when isShown(key) says
  // so. The full nav passes "this product's sections"; a lens passes its own list,
  // so a module a lens shows whole is this same markup with the same gates.
  const renderModuleSections = (isShown, key) => (
    <React.Fragment key={key}>
          {/* Agent Atlas — unified enterprise view (feature-flagged to org 3).
              "New navigation" on: the pillars draw its pages instead. */}
          {isShown('agent-atlas') && showNavSection('agent-atlas') && (pillarNav ? renderPillarNav() : renderSection('agent-atlas', t('nav:sections.agentAtlas'), 'globe', <>
            {/* Order is deliberate and owner-chosen: what AI exists (Atlas,
                Fleet, Users) → what needs attention (Alerts, Security) → how it
                is governed and what it costs (Compliance, Policy, FinOps,
                Responsible AI). The Security expander holds the eleven-page
                AI-SOC / SPM workspace, so it sits near the top rather than
                below the subgroups, where it used to be last in this section. */}
            {/* Inbox first: it is where the day starts — everything below it is a
                place to look something up. Its own feature gate, because the
                recommendation service is pre-GA and an org without it deployed
                would get an "unavailable" banner. */}
            {isFeatureEnabled('RECOMMENDATIONS') && (
              <li>
                <Link to="/agent-atlas/inbox" className={`nav-link ${isActive('/agent-atlas/inbox') ? 'active' : ''}`}>
                  <FeatherIcon name="inbox" size={18} className="nav-icon" />
                  <span>{t('layout:sidebarSidebar.recommendations')}</span>
                  {/* Null, not 0, when the count is unknown — see useInboxUnread. */}
                  {inboxUnread > 0 && <span className="nav-badge" title={`${inboxUnread} unread`}>{inboxUnread > 99 ? '99+' : inboxUnread}</span>}
                </Link>
              </li>
            )}
            <li>
              <Link to="/agent-atlas/atlas" className={`nav-link ${isActive('/agent-atlas/atlas') ? 'active' : ''}`}>
                <FeatherIcon name="map" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.atlas')}</span>
              </Link>
            </li>
            <li>
              <Link to="/agent-atlas/inventory" className={`nav-link ${isActive('/agent-atlas/inventory') ? 'active' : ''}`}>
                <FeatherIcon name="list" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.agentFleet')}</span>
              </Link>
            </li>
            <li>
              <Link to="/agent-atlas/users" className={`nav-link ${isActive('/agent-atlas/users') ? 'active' : ''}`}>
                <FeatherIcon name="users" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.users')}</span>
              </Link>
            </li>
            <li>
              <Link to="/agent-atlas/agent-network" className={`nav-link ${isActive('/agent-atlas/agent-network') ? 'active' : ''}`}>
                <FeatherIcon name="share-2" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.agentNetwork')}</span>
              </Link>
            </li>
            {/* Conversations, knowledge gaps and intent merged across every classified surface.
                Sits above Security: how the AI is used is read before what went wrong. */}
            {renderSubGroup('agent-atlas.adoption', t('nav:headings.adoption'), 'trending-up', [
              { to: '/agent-atlas/adoption/conversations', icon: 'message-square', label: t('layout:sidebarSidebar.conversations') },
              { to: '/agent-atlas/adoption/knowledge-gaps', icon: 'help-circle', label: t('layout:sidebarSidebar.knowledgeGaps') },
              { to: '/agent-atlas/adoption/intent-analysis', icon: 'target', label: t('layout:sidebarSidebar.intentAnalysis') },
            ])}
            {/* Click-to-open inline; reuses the same nav-subgroup toggle as
                Compliance / FinOps so the open behavior and the (collapsed-only,
                subtle) active state match rather than a full nav-link pill. */}
            <li className={`nav-subgroup atlas-sec-expander ${atlasSecurityOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className={`nav-subgroup-toggle ${SECURITY_ATLAS_ITEMS.some((it) => it.to && isActive(it.to)) ? 'has-active' : ''}`}
                onClick={() => setAtlasSecurityOpen((o) => {
                  const next = !o;
                  localStorage.setItem('sidebar_atlas_security_open', JSON.stringify(next));
                  return next;
                })}
                aria-expanded={atlasSecurityOpen}
              >
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.security')}</span>
                <FeatherIcon name={atlasSecurityOpen ? 'chevron-down' : 'chevron-right'} className="nav-subgroup-caret" />
              </button>
              {atlasSecurityOpen && (
                <ul className="atlas-sec-items">
                  {SECURITY_ATLAS_ITEMS.filter((it) => !it.orgIds || it.orgIds.includes(currentOrgId)).map((it) => (
                    it.headingKey ? (
                      <li key={`h-${it.headingKey}`} className="atlas-sec-subheading">{t(it.headingKey)}</li>
                    ) : (
                      <li key={it.to}>
                        <Link to={it.to} className={`nav-link atlas-sec-sublink ${isActive(it.to) ? 'active' : ''}`}>
                          <FeatherIcon name={it.icon} size={16} className="nav-icon" />
                          <span>{t(it.labelKey)}</span>
                          {navLock(it.to)}
                        </Link>
                      </li>
                    )
                  ))}
                </ul>
              )}
            </li>
            {/* Red-team audits, scheduled scans and model evaluations across the platform. After
                Security: testing an agent is the follow-up to what Security found. Gated like the
                tools themselves (monitoring or compliance). */}
            {canOpenCustomGovernance && renderSubGroup('agent-atlas.assessment', t('nav:sections.assessment'), 'check-square', [
              { to: '/agent-atlas/assessment', icon: 'grid', label: t('layout:sidebarSidebar.overview') },
              { to: '/agent-atlas/assessment/red-team', icon: 'shield', label: t('layout:sidebarSidebar.redTeamAudit') },
              { to: '/agent-atlas/assessment/schedules', icon: 'clock', label: t('layout:sidebarSidebar.scheduledScans') },
              { to: '/agent-atlas/assessment/evaluations', icon: 'box', label: t('layout:sidebarSidebar.modelEvaluation') },
            ])}
            {renderComplianceGroup('agent-atlas.compliance', { compliance: '/agent-atlas/compliance', auditReport: '/agent-atlas/compliance-audit' })}
            {/* Here Log Explorer is every surface's events in one stream. */}
            {renderLogExplorer('/agent-atlas/log-explorer')}
            <li>
              <Link to="/agent-atlas/policy" className={`nav-link ${isActive('/agent-atlas/policy') ? 'active' : ''}`}>
                <FeatherIcon name="lock" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.policy')}</span>
              </Link>
            </li>
            {/* Notifications sit under Policy in every module. */}
            <li>
              <Link to="/agent-atlas/alerts" className={`nav-link ${isActive('/agent-atlas/alerts') ? 'active' : ''}`}>
                <FeatherIcon name="bell" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.alerts')}</span>
              </Link>
            </li>
            {/* A single navigating entry, not a subgroup. `renderSubGroup` renders
                a button that only toggles — clicking "FinOps" showed three more
                links and no data, so reaching a number cost two clicks. This
                lands straight on the Tokenomics board; the other two views are
                tabs on that page. */}
            <li>
              <Link to="/agent-atlas/finops" className={`nav-link ${isActive('/agent-atlas/finops') ? 'active' : ''}`}>
                <FeatherIcon name="pie-chart" size={18} className="nav-icon" />
                <span>FinOps</span>
              </Link>
            </li>
          </>))}

          {/* Cloud Discovery follows the same labels, hierarchy and order as
              Gemini Enterprise. Its pages remain backed by cloud-native data. */}
          {isShown('cloud-ai') && showNavSection('cloud-ai') && renderSection('cloud-ai', t('nav:items.cloudDiscovery'), 'cloud', renderGeminiStyleNav('cloud-ai', getCloudDiscoveryNavGroups())
          )}

          {/* Custom Agents â€” one Gemini-style tree instead of five product sections. */}
          {isShown('custom-agents') && showNavSection('custom-agents') && renderSection('custom-agents', t('nav:items.customAgents'), 'cpu', customAgentsNavItems)}

          {/* Monitoring Section */}
          {isShown('monitoring') && showNavSection('monitoring') && renderSection('monitoring', t('nav:items.monitoring'), 'activity', monitoringNavItems)}

          {/* Security and Compliance Section */}
          {isShown('security') && showNavSection('security') && renderSection('security', t('nav:items.securityAndCompliance'), 'shield', <>
            <li>
              <Link to="/security/threat-control" className={`nav-link ${isActive('/security/threat-control') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.threatsControl')}</span>
              </Link>
            </li>
            <li>
              <Link to="/security/data-security" className={`nav-link ${isActive('/security/data-security') ? 'active' : ''}`}>
                <FeatherIcon name="lock" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.dataSecurity')}</span>
              </Link>
            </li>
            <li>
              <Link to="/security/threat-intelligence" className={`nav-link ${isActive('/security/threat-intelligence') ? 'active' : ''}`}>
                <FeatherIcon name="target" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.threatIntelligence')}</span>
              </Link>
            </li>
            <li>
              <Link to="/security/attack-path" className={`nav-link ${isActive('/security/attack-path') ? 'active' : ''}`}>
                <FeatherIcon name="git-branch" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.attackPath')}</span>
              </Link>
            </li>
            <li>
              <Link to="/security/audit-log" className={`nav-link ${isActive('/security/audit-log') ? 'active' : ''}`}>
                <FeatherIcon name="file-text" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.auditLog')}</span>
              </Link>
            </li>
            <li>
              <Link to="/security/alerts" className={`nav-link ${isActive('/security/alerts') ? 'active' : ''}`}>
                <FeatherIcon name="bell" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.alerts')}</span>
              </Link>
            </li>
            {/* Security Insights hidden - redundant with Threat Control Center */}
            {/* Audit Report is the second tab of Compliance; /admin/audit-report redirects there. */}
            <li>
              <Link to="/admin/compliance" className={`nav-link ${isActive('/admin/compliance') ? 'active' : ''}`}>
                <FeatherIcon name="clipboard" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.compliance')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/custom-guardrails" className={`nav-link ${isActive('/admin/custom-guardrails') ? 'active' : ''}`}>
                <FeatherIcon name="sliders" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.customGuardrails')}</span>
              </Link>
            </li>
          </>)}

          {/* FinOps Section */}
          {isShown('finops') && showNavSection('finops') && renderSection('finops', t('nav:items.finops'), 'trending-up', <>
            {/* Tokenomics is the second tab of Usage & ROI; /finops/tokenomics redirects there. */}
            <li>
              <Link
                to={{ pathname: '/finops/usage-roi', search: finopsPreservedSearch || undefined }}
                className={`nav-link ${isActive('/finops/usage-roi') ? 'active' : ''}`}
              >
                <FeatherIcon name="dollar-sign" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.usageRoi')}</span>
              </Link>
            </li>
            <li>
              <Link to="/finops/alerts" className={`nav-link ${isActive('/finops/alerts') ? 'active' : ''}`}>
                <FeatherIcon name="bell" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.alerts')}</span>
              </Link>
            </li>
            <li>
              <Link to="/finops/settings" className={`nav-link ${isActive('/finops/settings') ? 'active' : ''}`}>
                <FeatherIcon name="settings" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.finopsSettings')}</span>
              </Link>
            </li>
          </>)}

          {/* Evaluations Section */}
          {isShown('model-selection') && showNavSection('model-selection') && renderSection('model-selection', t('nav:items.evaluations'), 'check-square', <>
            <li>
              <Link to="/model-evaluation/projects" className={`nav-link ${isActive('/model-evaluation') ? 'active' : ''}`}>
                <FeatherIcon name="box" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.modelEvaluation')}</span>
              </Link>
            </li>
            <li>
              <Link to="/red-team-scanner" className={`nav-link ${isActive('/red-team-scanner') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.redTeamAudit')}</span>
              </Link>
            </li>
            <li>
              <Link to="/red-team/scheduled-scans" className={`nav-link ${isActive('/red-team/scheduled-scans') ? 'active' : ''}`}>
                <FeatherIcon name="clock" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.scheduledScans')}</span>
              </Link>
            </li>
          </>)}

          {/* Governance & Control Section (Custom Agents) */}
          {isShown('governance') && showNavSection('governance') && renderSection('governance', t('nav:items.governanceAndControl'), 'settings', <>
            <li>
              <Link to="/admin/policies" className={`nav-link ${isActive('/admin/policies') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.policies')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/agent-behavior" className={`nav-link ${isActive('/admin/agent-behavior') ? 'active' : ''}`}>
                <FeatherIcon name="cpu" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.agentProfiles')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/policy-audit-log" className={`nav-link ${isActive('/admin/policy-audit-log') ? 'active' : ''}`}>
                <FeatherIcon name="file-text" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.policyAuditLog')}</span>
              </Link>
            </li>
          </>)}

          {/* Shadow AI (consolidated) — the browser extension, MDM, EDR, the inline
              gateway and the SWG feed as one module. The four sections below stay until phase 4 turns
              their routes into redirects. Labels are the shared nav.json strings. */}
          {isShown('shadow-ai') && showNavSection('shadow-ai') && renderSection('shadow-ai', t('nav:items.endpointSecurity'), 'eye', <>
            <li>
              <Link to="/endpoint-security" className={`nav-link ${isActive('/endpoint-security') ? 'active' : ''}`}>
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{t('nav:items.dashboard')}</span>
              </Link>
            </li>
            <li>
              <Link to="/endpoint-security/endpoints" className={`nav-link ${isActive('/endpoint-security/endpoints') ? 'active' : ''}`}>
                <FeatherIcon name="monitor" size={18} className="nav-icon" />
                <span>{t('nav:items.endpoints')}</span>
              </Link>
            </li>
            <li>
              <Link to="/endpoint-security/users" className={`nav-link ${isActive('/endpoint-security/users') ? 'active' : ''}`}>
                <FeatherIcon name="users" size={18} className="nav-icon" />
                <span>{t('nav:items.users')}</span>
              </Link>
            </li>
            {/* Security: the shared Atlas security pages pinned to Endpoint Security
                (routes under /endpoint-security/security/*), grouped the way Gemini
                Enterprise groups its own, plus the module's DLP Analytics. */}
            {/* Adoption: the Atlas adoption pages pinned to Endpoint Security. Above
                Security, as in Atlas: how AI is used is read before what went wrong. */}
            {renderSubGroup('shadow-ai.adoption', t('nav:headings.adoption'), 'trending-up', [
              { to: '/endpoint-security/adoption/conversations', icon: 'message-square', label: t('layout:sidebarSidebar.conversations') },
              { to: '/endpoint-security/adoption/knowledge-gaps', icon: 'help-circle', label: t('layout:sidebarSidebar.knowledgeGaps') },
              { to: '/endpoint-security/adoption/intent-analysis', icon: 'target', label: t('layout:sidebarSidebar.intentAnalysis') },
            ])}
            {renderSubGroup('shadow-ai.security', t('nav:headings.security'), 'shield', [
              { to: '/endpoint-security/security/signals', icon: 'activity', label: t('nav:items.signals') },
              { to: '/endpoint-security/security/hunt', icon: 'search', label: t('nav:items.hunt') },
              { to: '/endpoint-security/security/incidents', icon: 'alert-octagon', label: t('nav:items.incidents') },
              { to: '/endpoint-security/security/skills', icon: 'layers', label: t('nav:items.skills') },
              { to: '/endpoint-security/security/response', icon: 'zap', label: t('nav:items.response') },
              { to: '/endpoint-security/security/exceptions', icon: 'slash', label: t('nav:items.exceptions') },
              { to: '/endpoint-security/security/ai-bom', icon: 'package', label: t('nav:items.aiBomInventory') },
              { to: '/endpoint-security/dlp-analytics', icon: 'shield', label: t('nav:items.dlpAnalytics') },
              { to: '/endpoint-security/guardrails', icon: 'filter', label: t('nav:items.guardrailDetections') },
            ])}
            {renderComplianceGroup('endpoint-security.compliance', { compliance: '/endpoint-security/compliance', auditReport: '/endpoint-security/audit-report' })}
            {renderLogExplorer('/endpoint-security/log-explorer')}
            <li>
              <Link to="/endpoint-security/policy" className={`nav-link ${isActive('/endpoint-security/policy') ? 'active' : ''}`}>
                <FeatherIcon name="lock" size={18} className="nav-icon" />
                <span>{t('nav:items.policy')}</span>
              </Link>
            </li>
            {/* Notifications sit under Policy in every module; the AI app catalogue and
                Coverage, Endpoint's own pages, follow them. */}
            {renderNavLink({ to: '/endpoint-security/alerts', icon: 'bell', label: t('nav:items.alerts') })}
            <li>
              <Link to="/endpoint-security/tool-catalog" className={`nav-link ${isActive('/endpoint-security/tool-catalog') ? 'active' : ''}`}>
                <FeatherIcon name="package" size={18} className="nav-icon" />
                <span>AI app catalogue</span>
              </Link>
            </li>
            <li>
              <Link to="/endpoint-security/coverage" className={`nav-link ${isActive('/endpoint-security/coverage') ? 'active' : ''}`}>
                <FeatherIcon name="target" size={18} className="nav-icon" />
                <span>{t('nav:items.coverage')}</span>
              </Link>
            </li>
          </>)}

          {/* Browser Endpoint Section (Endpoint Security / Shadow AI) */}
          {isShown('endpoint-management') && showNavSection('endpoint-management') && renderSection('endpoint-management', t('nav:items.browserEndpoint'), 'monitor', <>
            <li>
              <Link to="/admin/fleet-overview" className={`nav-link ${isActive('/admin/fleet-overview') ? 'active' : ''}`}>
                <FeatherIcon name="monitor" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.fleetOverview')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/shadow-ai-discovery" className={`nav-link ${isActive('/admin/shadow-ai-discovery') ? 'active' : ''}`}>
                <FeatherIcon name="eye" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.shadowAiDiscovery')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/ai-conversations" className={`nav-link ${isActive('/admin/ai-conversations') ? 'active' : ''}`}>
                <FeatherIcon name="message-square" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.aiConversations')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/dlp-analytics" className={`nav-link ${isActive('/admin/dlp-analytics') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.dlpAnalytics')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/endpoint-violations" className={`nav-link ${isActive('/admin/endpoint-violations') ? 'active' : ''}`}>
                <FeatherIcon name="shield-off" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.allViolations')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/endpoint-user-activity" className={`nav-link ${isActive('/admin/endpoint-user-activity') ? 'active' : ''}`}>
                <FeatherIcon name="users" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.userActivity')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/ai-tool-catalog" className={`nav-link ${isActive('/admin/ai-tool-catalog') ? 'active' : ''}`}>
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.aiToolCatalog')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/endpoint-alerts" className={`nav-link ${isActive('/admin/endpoint-alerts') ? 'active' : ''}`}>
                <FeatherIcon name="bell" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.endpointAlerts')}</span>
              </Link>
            </li>
            {canManageEndpointSettings && (
              <li>
                <Link to="/admin/endpoint-settings" className={`nav-link ${isActive('/admin/endpoint-settings') ? 'active' : ''}`}>
                  <FeatherIcon name="settings" size={18} className="nav-icon" />
                  <span>{t('layout:sidebarSidebar.settings')}</span>
                </Link>
              </li>
            )}
          </>)}

          {/* Managed Devices (Jamf / Intune) — feature-flagged via FLAGS.MDM.
              The agentless half of Shadow AI: MDM inventory shows what is INSTALLED
              on every managed device without deploying anything, complementing the
              browser section above which shows what is USED. */}
          {isShown('mdm') && showNavSection('mdm') && renderSection('mdm', t('nav:items.managedDevices'), 'hard-drive', <>
            <li>
              <Link to="/admin/mdm/overview" className={`nav-link ${isActive('/admin/mdm/overview') ? 'active' : ''}`}>
                <FeatherIcon name="hard-drive" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.overview')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/mdm/devices" className={`nav-link ${isActive('/admin/mdm/devices') ? 'active' : ''}`}>
                <FeatherIcon name="list" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.managedDevices')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/mdm/software" className={`nav-link ${isActive('/admin/mdm/software') ? 'active' : ''}`}>
                <FeatherIcon name="package" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.aiSoftware')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/mdm/mcp" className={`nav-link ${isActive('/admin/mdm/mcp') ? 'active' : ''}`}>
                <FeatherIcon name="link-2" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.mcpLocalModels')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/mdm/policy" className={`nav-link ${isActive('/admin/mdm/policy') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.policyRemediation')}</span>
              </Link>
            </li>
          </>)}

          {/* EDR — CrowdStrike Falcon + Microsoft Defender for Endpoint runtime
              signals (process, extensions, alerts). Connect sources under
              Management → Integrations → EDR. Feature-flagged via FLAGS.EDR. */}
          {isShown('edr') && showNavSection('edr') && renderSection('edr', t('nav:items.edr'), 'shield', <>
            <li>
              <Link to="/admin/edr/overview" className={`nav-link ${isActive('/admin/edr/overview') ? 'active' : ''}`}>
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.overview')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/edr/endpoints" className={`nav-link ${isActive('/admin/edr/endpoints') ? 'active' : ''}`}>
                <FeatherIcon name="monitor" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.endpoints')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/edr/processes" className={`nav-link ${isActive('/admin/edr/processes') ? 'active' : ''}`}>
                <FeatherIcon name="cpu" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.aiProcesses')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/edr/extensions" className={`nav-link ${isActive('/admin/edr/extensions') ? 'active' : ''}`}>
                <FeatherIcon name="package" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.browserExtensions')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/edr/mcp" className={`nav-link ${isActive('/admin/edr/mcp') ? 'active' : ''}`}>
                <FeatherIcon name="link-2" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.mcpLocalModels')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/edr/alerts" className={`nav-link ${isActive('/admin/edr/alerts') ? 'active' : ''}`}>
                <FeatherIcon name="bell" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.alerts')}</span>
              </Link>
            </li>
          </>)}

          {/* AI Gateway — feature-flagged via FLAGS.AI_GATEWAY in src/utils/featureFlags.js,
              which is currently allowedOrgIds: ['*'], so every org sees this section.
              Narrow it there by listing org_ids; this comment used to say "org 3 only"
              long after the flag had been opened up. */}
          {isShown('ai-gateway') && showNavSection('ai-gateway') && renderSection('ai-gateway', t('nav:items.aiGateway'), 'shield', <>
            <li>
              <Link to="/admin/ai-gateway" className={`nav-link ${isActive('/admin/ai-gateway') ? 'active' : ''}`}>
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.overview')}</span>
              </Link>
            </li>
            {/* "Live Events" → "Log Explorer": we now ingest logs from existing
                gateways/proxies (Netskope, Zscaler, Kong, …) and analyze them —
                the feed is ingested, not live. */}
            <li>
              <Link to="/admin/ai-gateway/events" className={`nav-link ${isActive('/admin/ai-gateway/events') ? 'active' : ''}`}>
                <FeatherIcon name="search" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.logExplorer')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/ai-gateway/policy" className={`nav-link ${isActive('/admin/ai-gateway/policy') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.policies')}</span>
              </Link>
            </li>
            {/* "Usage & Cost" → "API Usage": dollar metering came from the inline
                gateway (shelved); this now focuses on consumption/token volume. */}
            <li>
              <Link to="/admin/ai-gateway/usage" className={`nav-link ${isActive('/admin/ai-gateway/usage') ? 'active' : ''}`}>
                <FeatherIcon name="trending-up" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.apiUsage')}</span>
              </Link>
            </li>
            {/* Configuration stays hidden: it configures the inline data plane's
                provider credentials and tenant settings, and that page has not
                been revisited since the log-ingestion pivot.
            <li>
              <Link to="/admin/ai-gateway/configuration" className={`nav-link ${isActive('/admin/ai-gateway/configuration') ? 'active' : ''}`}>
                <FeatherIcon name="settings" size={18} className="nav-icon" />
                <span>Configuration</span>
              </Link>
            </li>
            */}
            {/* Keys was hidden with the rest of the inline data plane, but it is
                where a developer key is issued -- the first thing a new admin has
                to do, and step one of the integration docs. The gateway is live
                again, so a page with no link meant that first step could only be
                reached by typing the URL from memory. */}
            <li>
              <Link to="/admin/ai-gateway/keys" className={`nav-link ${isActive('/admin/ai-gateway/keys') ? 'active' : ''}`}>
                <FeatherIcon name="key" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.keysAccess')}</span>
              </Link>
            </li>
          </>)}

          {/* Microsoft menus share the Custom Agents and Gemini grouping. */}
          {isShown('copilot-studio') && showNavSection('copilot-studio') && renderSection('copilot-studio', t('nav:items.copilotStudio'), 'zap', renderMicrosoftNav(true)
          )}
          {isShown('enterprise-ai') && showNavSection('enterprise-ai') && renderSection('enterprise-ai', t('nav:items.m365Copilot'), 'cpu', renderMicrosoftNav(false)
          )}
          {/* Gemini Enterprise Agents */}
          {isShown('gemini-enterprise-agents') && showNavSection('gemini-enterprise-agents') && renderSection('gemini-enterprise-agents', t('nav:items.geminiEnterprise'), 'cpu', <>
            <li>
              <Link to={GEMINI_ENTERPRISE_DASHBOARD_PATH} className={`nav-link ${isActive(GEMINI_ENTERPRISE_DASHBOARD_PATH) ? 'active' : ''}`}>
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{GEMINI_ENTERPRISE_DASHBOARD_LABEL}</span>
              </Link>
            </li>
            {/* Recommendations is the "what should I do next" entry point, and
                Policy is the enforcement plane rather than one more security
                report — both read as top-level rather than buried in a group. */}
            <li>
              <Link to="/gemini/agents/recommendations" className={`nav-link ${isActive('/gemini/agents/recommendations') ? 'active' : ''}`}>
                <FeatherIcon name="zap" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.recommendations')}</span>
              </Link>
            </li>
            {/* The estate itself — the fleet, its wiring and the people using it —
                sits at the top rather than inside a group. "Agents & Data" is gone:
                with Source Intelligence moved under Adoption it held only these two,
                so the group was a click in front of them and nothing else. */}
            <li>
              <Link
                to="/gemini/agents/registry"
                className={`nav-link ${isActive('/gemini/agents/registry') ? 'active' : ''}`}
                title={t('layout:sidebarSidebar.agentRegistryDescription')}
              >
                <FeatherIcon name="server" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.agentRegistry')}</span>
              </Link>
            </li>
            <li>
              <Link to="/gemini/agents/users" className={`nav-link ${isActive('/gemini/agents/users') ? 'active' : ''}`}>
                <FeatherIcon name="users" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.users')}</span>
              </Link>
            </li>
            <li>
              <Link to="/gemini/agents/agent-network" className={`nav-link ${isActive('/gemini/agents/agent-network') ? 'active' : ''}`}>
                <FeatherIcon name="share-2" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.agentNetwork')}</span>
              </Link>
            </li>
            {/* Grouped by the job each page does, using
                the same renderSubGroup helper as Compliance and FinOps, so the
                open/close behaviour and active-child highlighting match the rest
                of this sidebar.
                Nothing was dropped: every link that was here is still here, and
                Applications / Coverage Atlas are added — both were routed and
                previously reachable only by typing the URL.
                Sub-tabs keep no entry of their own: Tokenomics inside Usage & ROI,
                Violations inside Policy. */}
            {renderSubGroup('gemini-enterprise-agents.adoption', t('nav:headings.adoption'), 'trending-up', [
              { to: '/gemini/agents/usage-roi', icon: 'trending-up', label: t('nav:items.usageAndRoi') },
              { to: '/gemini/agents/conversations', icon: 'message-circle', label: t('nav:items.conversations') },
              { to: '/gemini/agents/knowledge-gaps', icon: 'book-open', label: t('nav:items.knowledgeGaps') },
              { to: '/gemini/agents/intent-analysis', icon: 'target', label: t('nav:items.intentAnalysis') },
              { to: '/gemini/agents/sources', icon: 'database', label: t('nav:items.sourceIntelligence') },
              // Applications and Coverage Atlas stay hidden from nav; their routes
              // remain live so existing links keep working.
            ])}
            {renderSubGroup('gemini-enterprise-agents.security', t('nav:headings.security'), 'shield', [
              { to: '/gemini/agents/security/signals', icon: 'activity', label: t('nav:items.signals') },
              { to: '/gemini/agents/security/hunt', icon: 'search', label: t('nav:items.hunt') },
              { to: '/gemini/agents/security/incidents', icon: 'alert-octagon', label: t('nav:items.incidents') },
              { to: '/gemini/agents/security/skills', icon: 'layers', label: t('nav:items.skills') },
              { to: '/gemini/agents/security/response', icon: 'zap', label: t('nav:items.response') },
              { to: '/gemini/agents/security/exceptions', icon: 'slash', label: t('nav:items.exceptions') },
              { to: '/gemini/agents/security/ai-bom', icon: 'package', label: t('nav:items.aiBomInventory') },
              { to: '/gemini/agents/posture', icon: 'lock', label: t('nav:items.accessManagement') },
            ])}
            {renderComplianceGroup('gemini-enterprise-agents.compliance', { compliance: '/gemini/agents/compliance', auditReport: '/gemini/agents/audit-report' })}
            {renderLogExplorer('/gemini/agents/log-explorer')}
            {/* Policy — its own top-level link (not under Security), after the security/governance sections. */}
            {isFeatureEnabled('GEMINI_ENTERPRISE_POLICY') && (
              <li>
                <Link to="/gemini/agents/policy" className={`nav-link ${isActive('/gemini/agents/policy') ? 'active' : ''}`}>
                  <FeatherIcon name="shield" size={18} className="nav-icon" />
                  <span>{t('layout:sidebarSidebar.policy')}</span>
                </Link>
              </li>
            )}
            {/* Notifications sit under Policy in every module. */}
            {renderNavLink({ to: '/gemini/agents/alerts', icon: 'bell', label: t('nav:items.alerts') })}
            <li>
              <Link to="/gemini/agents/settings" className={`nav-link ${isActive('/gemini/agents/settings') ? 'active' : ''}`}>
                <FeatherIcon name="settings" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.settings')}</span>
              </Link>
            </li>
          </>)}

          {/* Gemini Workspace is a separate product so its overlapping route
              labels never appear beside the Enterprise navigation tree. */}
          {isShown('gemini-workspace') && showNavSection('gemini-workspace') && renderSection('gemini-workspace', t('nav:items.geminiWorkspace'), 'sun', <>
            <li>
              <Link to={GEMINI_WORKSPACE_DASHBOARD_PATH} className={`nav-link ${isActive(GEMINI_WORKSPACE_DASHBOARD_PATH) ? 'active' : ''}`}>
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{GEMINI_WORKSPACE_DASHBOARD_LABEL}</span>
              </Link>
            </li>
            <li>
              <Link to="/gemini/usage-roi" className={`nav-link ${isActive('/gemini/usage-roi') ? 'active' : ''}`}>
                <FeatherIcon name="trending-up" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.usageRoi')}</span>
              </Link>
            </li>
            <li>
              <Link to="/gemini/users" className={`nav-link ${isActive('/gemini/users') ? 'active' : ''}`}>
                <FeatherIcon name="users" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.users')}</span>
              </Link>
            </li>
            <li>
              <Link to="/gemini/intent-analysis" className={`nav-link ${isActive('/gemini/intent-analysis') ? 'active' : ''}`}>
                <FeatherIcon name="target" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.intentAnalysis')}</span>
              </Link>
            </li>
            <li>
              <Link to="/gemini/security/signals" className={`nav-link ${isActive('/gemini/security/signals') ? 'active' : ''}`}>
                <FeatherIcon name="alert-triangle" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.threats')}</span>
              </Link>
            </li>
            <li>
              <Link
                to="/gemini/applications"
                className={`nav-link ${isActive('/gemini/applications') ? 'active' : ''}`}
                title={t('layout:sidebarSidebar.workspaceAppsDescription')}
              >
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.workspaceApps')}</span>
              </Link>
            </li>
            {renderComplianceGroup('gemini-workspace.compliance', { compliance: '/gemini/compliance', auditReport: '/gemini/audit-report' })}
            {renderLogExplorer('/gemini/log-explorer')}
            <li>
              <Link to="/gemini/alerts" className={`nav-link ${isActive('/gemini/alerts') ? 'active' : ''}`}>
                <FeatherIcon name="bell" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.alerts')}</span>
              </Link>
            </li>
            <li>
              <Link to="/gemini/settings" className={`nav-link ${isActive('/gemini/settings') ? 'active' : ''}`}>
                <FeatherIcon name="settings" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.settings')}</span>
              </Link>
            </li>
          </>)}

          {/* AI Providers */}
          {isShown('saas-ai-platform') && showNavSection('saas-ai-platform') && renderSection('saas-ai-platform', t('nav:items.aiProviders'), 'cpu', renderGeminiStyleNav('saas-ai-platform', getSaasAiNavGroups())
          )}

          {/* SAST / Code Security */}
          {isShown('sast') && showNavSection('sast') && renderSection('sast', t('nav:items.codeSecurity'), 'shield', <>
            <li>
              <Link to="/sast/overview" className={`nav-link ${isActive('/sast/overview') ? 'active' : ''}`}>
                <FeatherIcon name="grid" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.dashboard')}</span>
              </Link>
            </li>
            {/* Code Security's findings from the recommendation engine, behind the same
                pre-GA gate as the Atlas Inbox. */}
            {isFeatureEnabled('RECOMMENDATIONS') && (
              <li>
                <Link to="/sast/recommendations" className={`nav-link ${isActive('/sast/recommendations') ? 'active' : ''}`}>
                  <FeatherIcon name="inbox" size={18} className="nav-icon" />
                  <span>{t('layout:sidebarSidebar.recommendations')}</span>
                  {codeSecurityUnread > 0 && <span className="nav-badge" title={`${codeSecurityUnread} unread`}>{codeSecurityUnread > 99 ? '99+' : codeSecurityUnread}</span>}
                </Link>
              </li>
            )}
            <li>
              <Link to="/sast/repositories" className={`nav-link ${isActive('/sast/repositories') ? 'active' : ''}`}>
                <FeatherIcon name="git-branch" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.repositories')}</span>
              </Link>
            </li>
            <li>
              <Link to="/sast/findings" className={`nav-link ${isActive('/sast/findings') ? 'active' : ''}`}>
                <FeatherIcon name="alert-circle" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.findings')}</span>
              </Link>
            </li>
            {/* Security: the shared Atlas security pages pinned to SAST
                (routes under /sast/security/*), grouped the way Endpoint Security groups its own. */}
            {renderSubGroup('sast.security', t('nav:headings.security'), 'shield', [
              { to: '/sast/security/signals', icon: 'activity', label: t('nav:items.signals') },
              { to: '/sast/security/hunt', icon: 'search', label: t('nav:items.hunt') },
              { to: '/sast/security/incidents', icon: 'alert-octagon', label: t('nav:items.incidents') },
              { to: '/sast/security/skills', icon: 'layers', label: t('nav:items.skills') },
              { to: '/sast/security/response', icon: 'zap', label: t('nav:items.response') },
              { to: '/sast/security/exceptions', icon: 'slash', label: t('nav:items.exceptions') },
              { to: '/sast/security/ai-bom', icon: 'package', label: t('nav:items.aiBomInventory') },
            ])}
            {/* No Audit Report yet: the Atlas report does not read Code Security's scan evidence. */}
            {renderComplianceGroup('sast.compliance', { compliance: '/sast/compliance' })}
            <li>
              <Link to="/sast/policy" className={`nav-link ${isActive('/sast/policy') ? 'active' : ''}`}>
                <FeatherIcon name="lock" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.policy')}</span>
              </Link>
            </li>
            <li>
              <Link to="/sast/settings" className={`nav-link ${isActive('/sast/settings') ? 'active' : ''}`}>
                <FeatherIcon name="settings" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.settings')}</span>
              </Link>
            </li>
          </>)}
          {/* Divider before always-visible sections */}
          {ALWAYS_SHOWN_SECTIONS.some(isShown) && <div className="product-sections-divider" />}

          {/* Management Section - Administrator only. With "New navigation" on, these
              pages are tabs on Settings in the account menu (pillarTabs.js `settings`),
              or settings of the feature they configure (Inbox, Notifications, Incidents). */}
          {isShown('management') && !pillarNav && renderSection('management', t('nav:sections.management'), 'tool', <>
            <li>
              <Link to="/admin/agents" className={`nav-link ${isActive('/admin/agents') ? 'active' : ''}`}>
                <FeatherIcon name="user-check" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.agents')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/teams" className={`nav-link ${isActive('/admin/teams') ? 'active' : ''}`}>
                <FeatherIcon name="briefcase" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.teams')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/users" className={`nav-link ${isActive('/admin/users') ? 'active' : ''}`}>
                <FeatherIcon name="users" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.managementUsers')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/custom-roles" className={`nav-link ${isActive('/admin/custom-roles') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.rolesPermissions')}</span>
              </Link>
            </li>
            {currentOrgId === 3 && (
              <li>
                <Link to="/admin/recommendation-rules" className={`nav-link ${isActive('/admin/recommendation-rules') ? 'active' : ''}`}>
                  <FeatherIcon name="target" size={18} className="nav-icon" />
                  <span>{t('layout:sidebarSidebar.recommendationRules')}</span>
                </Link>
              </li>
            )}
            <li>
              <Link to="/admin/sso" className={`nav-link ${isActive('/admin/sso') ? 'active' : ''}`}>
                <FeatherIcon name="key" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.ssoSettings')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/api-keys" className={`nav-link ${isActive('/admin/api-keys') ? 'active' : ''}`}>
                <FeatherIcon name="lock" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.apiKeys')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/data-policy" className={`nav-link ${isActive('/admin/data-policy') ? 'active' : ''}`}>
                <FeatherIcon name="shield" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.dataPolicy')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/ai-provider" className={`nav-link ${isActive('/admin/ai-provider') ? 'active' : ''}`}>
                <FeatherIcon name="cpu" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.aiProvider')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/integrations" className={`nav-link ${isActive('/admin/integrations') ? 'active' : ''}`}>
                <FeatherIcon name="link" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.integrations')}</span>
              </Link>
            </li>
            <li>
              <Link to="/admin/alert-configuration" className={`nav-link ${isActive('/admin/alert-configuration') ? 'active' : ''}`}>
                <FeatherIcon name="bell" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.alertConfiguration')}</span>
              </Link>
            </li>
            <li>
              <Link to="/security-atlas/automation" className={`nav-link ${isActive('/security-atlas/automation') ? 'active' : ''}`}>
                <FeatherIcon name="sliders" size={18} className="nav-icon" />
                <span>{t('nav:items.socConfiguration')}</span>
              </Link>
            </li>
            <li>
              {/* Who changed what in the platform (admin actions), not the modules' event logs
                  (Log Explorer): named for what it is, as its page is. */}
              <Link to="/admin/audit-log" className={`nav-link ${isActive('/admin/audit-log') ? 'active' : ''}`}>
                <FeatherIcon name="file-text" size={18} className="nav-icon" />
                <span>{t('nav:items.platformAuditLog')}</span>
              </Link>
            </li>
          </>, { adminOnly: true })}

          {/* Custom Dashboards Section */}
          {isShown('custom-dashboards') && showNavSection('custom-dashboards') && renderSection('custom-dashboards', t('nav:items.customDashboards'), 'layout', <>
            <li>
              <Link to="/ai-explorer/dashboards" className={`nav-link ${isActive('/ai-explorer/dashboards') ? 'active' : ''}`}>
                <FeatherIcon name="layout" size={18} className="nav-icon" />
                <span>{t('layout:sidebarSidebar.aiDashboards')}</span>
              </Link>
            </li>
          </>, { link: '/ai-explorer/dashboards' })}

    </React.Fragment>
  );
  // The full nav: every surface the user has ticked (by default the ones the org
  // is licensed for), the section holding the current page even if unticked,
  // and the always-shown sections. Sections no product lists stay retired.
  // With "New navigation" on, the pillars (Atlas) are the whole sidebar: no product
  // module sections, no Custom Dashboards, and Management is Settings in the account menu.
  const inNav = (key) => (pillarNav ? key === 'agent-atlas'
    : ALWAYS_SHOWN_SECTIONS.includes(key)
      || (Boolean(NAV_SECTIONS[key]?.surface) && (surfaces.isShown(key) || sectionHoldsPath(key))));

  return (
    <NavPinsContext.Provider value={pinsApi}>
    <nav className={sidebarClasses} style={{ height: '100vh' }} aria-label={t('layout:sidebarSidebar.primary')}>
      <div className="sidebar-main" ref={sidebarScrollRef}>
        {!brandInHeader && (
        <div className="sidebar-header">
          <div className="sidebar-header-content">
            {showTextLogo ? (
              <h2 className="logo">
                SAF<span className="three">3</span><span className="accent">{t('layout:sidebarSidebar.ai')}</span>
              </h2>
            ) : (
              <>
                {/* Accept both absolute (http/https) and relative (/) URLs from the nginx proxy */}
                <img
                  src={logoUrl}
                  alt={t('layout:sidebarSidebar.organizationLogo')}
                  className="org-logo"
                  onError={handleImageError}
                  onLoad={() => console.log('[Sidebar Logo] <img> loaded successfully! src:', logoUrl)}
                  style={{
                    maxWidth: '200px',
                    maxHeight: '60px',
                    objectFit: 'contain',
                    display: 'block',
                    margin: '0 auto',
                  }}
                />
                <div className="powered-by">
                  {t('layout:sidebarSidebar.poweredBy')} <span className="powered-by-brand">{t('layout:sidebarSidebar.brandSaf')}<span className="powered-by-three">3</span><span className="powered-by-accent">{t('layout:sidebarSidebar.ai2')}</span></span>
                </div>
              </>
            )}
          </div>
          {isMobile && (
            <button
              className="sidebar-close-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (onMobileClose) {
                  onMobileClose();
                }
              }}
              aria-label={t('layout:sidebarSidebar.closeSidebar')}
              type="button"
            >
              <FeatherIcon name="x" size={18} />
            </button>
          )}
        </div>
        )}

        {/* Axon AI Pill */}
        <div style={{ marginBottom: '12px' }}>
          <Link
            to="/ai-explorer"
            className={`axon-ai-pill ${isActive('/ai-explorer') ? 'active' : ''}`}
            style={{ gap: '1px' }}
          >
            <AgentMark size={18} className="nav-icon agent-mark" />
            <span>{t('layout:sidebarSidebar.saf3aiAgent')}</span>
          </Link>
        </div>

        {/* Pinned pages (favourites) — per browser. */}
        <PinnedNavBlock variant={!isMobile && isCollapsed ? 'rail' : 'list'} />

        {lensActive && (
          <>
            <LensNavCard lens={lens} chosenForRole={chosenForRole} rail={!isMobile && isCollapsed} />
            {lensGroups.map((g) => (
              <React.Fragment key={`lens-${lens.id}-${g.key}`}>
                {renderSection(
                  `lens-${lens.id}-${g.key}`,
                  g.label || t(`nav:${g.labelKey}`),
                  g.icon,
                  <>{g.items.map((it) => renderNavLink({ to: it.to, icon: it.icon, label: t(`nav:${it.labelKey}`) }))}</>,
                )}
              </React.Fragment>
            ))}
            {lensSectionKeys.size > 0 && renderModuleSections((key) => lensSectionKeys.has(key), `lens-sections-${lens.id}`)}
            <AllModulesToggle
              open={allModulesOpen}
              onToggle={() => setAllModulesOpen((v) => !v)}
              rail={!isMobile && isCollapsed}
            />
          </>
        )}

        {/* Action Center — hidden for now */}

        {/* The module navigation: the ticked surfaces (Surfaces, in the header's
            lens menu), behind "All modules" when a lens is on. */}
        {(!lensActive || allModulesOpen) && renderModuleSections(
          (key) => inNav(key) && !lensSectionKeys.has(key),
          'module-navigation',
        )}
      </div>

      <div className="sidebar-footer">

        <div className="sidebar-toggle d-flex align-items-center gap-4">
          <button
            type="button"
            id="sidebar-toggle-btn"
            className="nav-link"
            onClick={handleToggle}
            aria-label={isCollapsed ? "Expand Menu" : "Collapse Menu"}
          >
            <FeatherIcon name={isCollapsed ? "chevron-right" : "chevron-left"} className="nav-icon" />
          </button>
          <p className="copyright">{t('layout:sidebarSidebar.copyright', { year: new Date().getFullYear() })} <br /> {t('layout:sidebarSidebar.allRightsReserved')}</p>
        </div>
      </div>
    </nav>
    </NavPinsContext.Provider>
  );
};

export default Sidebar;
